import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            pt: { xs: 2.5, sm: 3.5 },
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const LoginForm: React.FC = () => {
  const { login, register } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  
  // Register form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phoneNumber: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(loginData.email, loginData.password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validar que todos los campos obligatorios estén completos
    if (!registerData.email || !registerData.password || !registerData.name || !registerData.phoneNumber) {
      setError('Todos los campos obligatorios son requeridos');
      setLoading(false);
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.name,
        registerData.phoneNumber,
        registerData.birthDate || null,
        registerData.birthTime || null,
        registerData.birthPlace || null
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const publicUrl = config.PUBLIC_URL || '';
  const textFieldSx = {
    '& label.Mui-focused': {
      color: '#f08b55',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: '#f08b55',
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `linear-gradient(135deg, rgba(15, 15, 35, 0.85) 0%, rgba(26, 26, 46, 0.85) 50%, rgba(22, 33, 62, 0.85) 100%), url(${publicUrl}/images/bg.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          width: '100%',
          px: { xs: 0, sm: 2 },
        }}
      >
        <Paper
          elevation={24}
          sx={{
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'min(90vh, 720px)', sm: 'auto' },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(90deg, #3B4C6B 0%, #48566B 100%)',
              color: 'white',
              p: { xs: 2.5, sm: 3.5 },
              textAlign: 'center',
            }}
          >
            <Box
              component="img"
              src={`${publicUrl}/images/logo.png`}
              alt="Portal Mystic Tarot"
              sx={{
                width: { xs: 72, sm: 96 },
                height: 'auto',
                mx: 'auto',
                mb: { xs: 1.5, sm: 2 },
              }}
            />
            <Typography
              variant="body1"
              component="div"
              sx={{
                opacity: 0.95,
                mt: { xs: 1.25, sm: 1.75 },
                lineHeight: 1.2,
                textAlign: 'center',
                maxWidth: '600px',
                mx: 'auto',
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.95rem', sm: '1rem' },
              }}
            >
              Si algo te inquieta, llegaste al lugar correcto. Regístrate GRATIS y disfruta 10 mensajes de bienvenida para conversar con nuestros guías espirituales.
              <br />
              <br />
              ✨ Este beneficio está disponible una sola vez por nuevo usuario.
              <br />
              Si ya estás registrado, solo debes ingresar tu correo y contraseña para iniciar sesión.
            </Typography>
          </Box>

          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'rgba(240, 139, 85, 0.3)',
              px: { xs: 1, sm: 0 },
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="auth tabs"
              variant="fullWidth"
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: '#f08b55', height: 3 },
              }}
              sx={{
                '.MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  '&.Mui-selected': {
                    color: '#f08b55',
                  },
                },
              }}
            >
              <Tab label="Iniciar Sesión" />
              <Tab label="Registrarse" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                label="Contraseña"
                type={showLoginPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {error && tabValue === 0 && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  backgroundColor: '#f08b55',
                  '&:hover': {
                    backgroundColor: '#d97947',
                  },
                  '&:disabled': {
                    backgroundColor: '#f0ab85',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
              </Button>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleRegister}>
              <TextField
                fullWidth
                label="Nombre"
                value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                label="Celular"
                type="tel"
                value={registerData.phoneNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Solo números
                  if (value.length <= 10) {
                    setRegisterData({ ...registerData, phoneNumber: value });
                  }
                }}
                margin="normal"
                required
                disabled={loading}
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]{10}',
                }}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                label="Contraseña"
                type={showRegisterPassword ? 'text' : 'password'}
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                margin="normal"
                required
                disabled={loading}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {error && tabValue === 1 && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  backgroundColor: '#f08b55',
                  '&:hover': {
                    backgroundColor: '#d97947',
                  },
                  '&:disabled': {
                    backgroundColor: '#f0ab85',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Registrarse'}
              </Button>
            </form>
          </TabPanel>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginForm;
